import { PICKUP_MANAGEMENT_MODES, FORM_VALIDATION_MSG } from './constants';
import { getReservationDetailsPayload } from './ReservationRequestorInfo/utils';
import { convertDateTimeToTimeString } from '@dataGrid/gridUtilities';
import { format, addDays } from 'date-fns';
import { getTerminalDateByOffset } from '../../../utils/utils';
import { getFilteredShipmentsData, getBulkGroupId, getRemovedShipmentsFromSnapshot } from './utils';
import { getPickupNumberIdListFromPickup } from '../GroupPickup/utils';

const getPickupDateTime = ({ readyTime, closeTime, pickupDate, route, dispatchZone }) => {
  return {
    readyTime: convertDateTimeToTimeString(readyTime),
    closeTime: convertDateTimeToTimeString(closeTime),
    pickupDate: format(pickupDate, 'yyyy-MM-dd'),
    route,
    dispatchZone: dispatchZone ? dispatchZone : null
  };
};

/** Fields shared by single EditPickup and BulkEditPickup request bodies. */
const buildEditPickupSharedRequestBody = ({
  formData,
  pickupTerminal,
  userProfile,
  isDateTimeUpdated,
  enableRequestorInfo
}) => ({
  shipper: {
    ...formData.shipperInfo,
    shipperContactPhone: formData.shipperInfo.shipperContactPhone.toString(),
    shipperSpecialInstructions: undefined
  },
  shipmentData: getFilteredShipmentsData(formData.shipmentInfo),
  ...getPickupDateTime(formData.pickupDateTimeInfo),
  reservationDetails: getReservationDetailsPayload(enableRequestorInfo, formData.requestorInfo),
  specialInstructions: formData.specialInstructions,
  isSwapPickup: formData.isSwapPickup,
  isDockDropPickup: formData.isDockDropPickup,
  isExhibitPickup: formData.isExhibitPickup,
  terminalCode: userProfile.terminal.code,
  editReasonInfo: isDateTimeUpdated
    ? {
        ...formData.editReasonInfo,
        ...(Boolean(formData.editReasonInfo.editReasonCode) && {
          editReasonCode: formData.editReasonInfo.editReasonCode,
          callTime: convertDateTimeToTimeString(formData.editReasonInfo.callTime)
        })
      }
    : null,
  pickupTerminal,
  isLiftGate: formData.isLiftGate
});

const PICKUP_MANAGEMENT_CONFIG = {
  [PICKUP_MANAGEMENT_MODES.CREATE_PICKUP]: {
    apiName: 'CreatePickup',
    getRequestParams: ({ pickupData, enableRequestorInfo }) => {
      const { pickupInfo: formData, pickupTerminal, userProfile } = pickupData;
      return {
        pickupTerminal,
        shipper: {
          ...formData.shipperInfo,
          shipperContactPhone: formData.shipperInfo.shipperContactPhone.toString(),
          shipperSpecialInstructions: undefined
        },
        shipmentData: getFilteredShipmentsData(formData.shipmentInfo).map((shipment, index) => ({
          ...shipment,
          sequenceNumber: (index + 1).toString()
        })),
        ...getPickupDateTime(formData.pickupDateTimeInfo),
        ...formData.billingInfo,
        reservationDetails: getReservationDetailsPayload(
          enableRequestorInfo,
          formData.requestorInfo
        ),
        specialInstructions: formData.specialInstructions,
        isSwapPickup: formData.isSwapPickup,
        isDockDropPickup: formData.isDockDropPickup,
        isExhibitPickup: formData.isExhibitPickup,
        terminalCode: userProfile.terminal.code,
        isLiftGate: formData.isLiftGate
      };
    },
    getPickupDateError: (value, terminalOffset) => {
      if (!value) {
        return FORM_VALIDATION_MSG.REQUIRED;
      }
      if (new Date(value) < new Date(getTerminalDateByOffset(new Date(), terminalOffset))) {
        return FORM_VALIDATION_MSG.MIN_PICKUP_DATE;
      }
      return null;
    }
  },
  [PICKUP_MANAGEMENT_MODES.EDIT_PICKUP]: {
    apiName: 'EditPickup',
    getRequestParams: ({ pickupData, enableRequestorInfo }) => {
      const {
        pickupInfo: formData,
        pickupTerminal,
        pickupId,
        pickupNumber,
        isDateTimeUpdated,
        userProfile
      } = pickupData;
      return {
        ...buildEditPickupSharedRequestBody({
          formData,
          pickupTerminal,
          userProfile,
          isDateTimeUpdated,
          enableRequestorInfo
        }),
        ...formData.billingInfo,
        pickupId,
        pickupNumber
      };
    },
    getPickupDateError: (value, terminalOffset) => {
      if (!value) {
        return FORM_VALIDATION_MSG.REQUIRED;
      }
      if (new Date(value) < new Date(getTerminalDateByOffset(new Date(), terminalOffset))) {
        return FORM_VALIDATION_MSG.MIN_PICKUP_DATE;
      } else if (
        new Date(value) > addDays(new Date(getTerminalDateByOffset(new Date(), terminalOffset)), 7)
      ) {
        return FORM_VALIDATION_MSG.MAX_PICKUP_DATE;
      }
      return null;
    }
  },
  [PICKUP_MANAGEMENT_MODES.BULK_EDIT_PICKUP]: {
    apiName: 'BulkEditPickup',
    getRequestParams: ({
      pickupData,
      selectedPickups,
      originalShipments,
      enableRequestorInfo
    }) => {
      const { pickupInfo: formData, pickupTerminal, userProfile, isDateTimeUpdated } = pickupData;
      const groupId = getBulkGroupId(selectedPickups);
      const removedShipments = getRemovedShipmentsFromSnapshot(
        originalShipments,
        formData.shipmentInfo
      );
      const { specialInstructions, ...sharedRequestBody } = buildEditPickupSharedRequestBody({
        formData,
        pickupTerminal,
        userProfile,
        isDateTimeUpdated,
        enableRequestorInfo
      });
      return {
        ...sharedRequestBody,
        reservationDetails: null,
        groupId,
        removedShipments
      };
    },
    getPickupDateError: (value, terminalOffset) => {
      if (!value) {
        return FORM_VALIDATION_MSG.REQUIRED;
      }
      if (new Date(value) < new Date(getTerminalDateByOffset(new Date(), terminalOffset))) {
        return FORM_VALIDATION_MSG.MIN_PICKUP_DATE;
      } else if (
        new Date(value) > addDays(new Date(getTerminalDateByOffset(new Date(), terminalOffset)), 7)
      ) {
        return FORM_VALIDATION_MSG.MAX_PICKUP_DATE;
      }
      return null;
    }
  },
  [PICKUP_MANAGEMENT_MODES.MERGE_PICKUP]: {
    apiName: 'MergePickup',
    getRequestParams: ({
      pickupData,
      selectedPickups,
      selectedDriver,
      isExhibitPickupInMergeMode,
      allPickups
    }) => {
      const { pickupInfo: formData, pickupTerminal, userProfile } = pickupData;
      return {
        shipper: {
          ...formData.shipperInfo,
          shipperContactPhone: formData.shipperInfo.shipperContactPhone.toString(),
          isAccountNumberDisabled: undefined,
          shipperSpecialInstructions: undefined
        },
        shipmentData: getFilteredShipmentsData(formData.shipmentInfo),
        ...getPickupDateTime(formData.pickupDateTimeInfo),
        ...formData.billingInfo,
        specialInstructions: formData.specialInstructions,
        isSwapPickup: formData.isSwapPickup,
        terminalCode: userProfile.terminal.code,
        pickupDriverMapping: selectedPickups.map(({ pickupId, driverId, groupId }) => ({
          pickupId,
          driverId,
          groupId
        })),
        pickupNumbers: [
          ...new Set(
            selectedPickups.flatMap((pickup) =>
              getPickupNumberIdListFromPickup(pickup).map(({ pickupNumber }) => pickupNumber)
            )
          )
        ],
        driverIdToAssignPickup: selectedDriver,
        pickupTerminal,
        isExhibitPickup: isExhibitPickupInMergeMode,
        retainedPickupId: formData.pickupId,
        isLiftGate: formData.isLiftGate
      };
    },
    getPickupDateError: (value, terminalOffset) => {
      if (!value) {
        return FORM_VALIDATION_MSG.REQUIRED;
      }
      if (new Date(value) < new Date(getTerminalDateByOffset(new Date(), terminalOffset))) {
        return FORM_VALIDATION_MSG.MIN_PICKUP_DATE;
      } else if (
        new Date(value) > addDays(new Date(getTerminalDateByOffset(new Date(), terminalOffset)), 7)
      ) {
        return FORM_VALIDATION_MSG.MAX_PICKUP_DATE;
      }
      return null;
    }
  }
};

const getAllPickupsNumbersForMergePickup = (selectedPickups, allPickups) => {
  // Fixme: Remove this iteration logic once we have grouped pickups data in the store
  const groupIds = new Set(selectedPickups.map((pickup) => pickup.groupId));
  const pickupNumbers = [];
  allPickups.forEach((pickup) => {
    if (groupIds.has(pickup.groupId)) {
      pickupNumbers.push(pickup.pickupNumber);
    }
  });
  return pickupNumbers;
};

export { PICKUP_MANAGEMENT_CONFIG, getPickupDateTime };
